import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { get } from 'api/request'
import { Provider } from 'types/provider'
import { mapProviderPeopleDocuments } from 'utils/providerTransforms'
import { providersKeys } from './keys'

const PROVISIONING_POLL_MS = 5000

export const getProviderRefetchInterval = (provider?: Provider): number | false => {
  if (!provider?.paymentProcessor?.accountId) {
    return false
  }
  if (provider.paymentSetup?.canAcceptPayments === true) {
    return false
  }
  return PROVISIONING_POLL_MS
}

export const fetchCurrentProvider = async (): Promise<Provider> => {
  const response = await get('/ui/providers')
  const rawProvider = response.data as Provider
  return mapProviderPeopleDocuments(rawProvider)
}

export const fetchCanDisconnectStripe = async (): Promise<boolean> => {
  const response = await get('/ui/providers/can-disconnect-stripe')
  return response.data?.canDisconnect === true
}

type CurrentProviderQueryOptions = Omit<
  UseQueryOptions<Provider, Error, Provider>,
  'queryKey' | 'queryFn'
> & {
  userId?: string
}

export const useCurrentProviderQuery = (queryOptions?: CurrentProviderQueryOptions) => {
  const { userId, ...options } = queryOptions ?? {}

  return useQuery({
    queryKey: providersKeys.current(userId),
    queryFn: fetchCurrentProvider,
    refetchInterval: query => getProviderRefetchInterval(query.state.data),
    ...options,
  })
}

type CanDisconnectStripeQueryOptions = Omit<
  UseQueryOptions<boolean, Error, boolean>,
  'queryKey' | 'queryFn'
> & {
  userId?: string
}

export const useCanDisconnectStripeQuery = (queryOptions?: CanDisconnectStripeQueryOptions) => {
  const { userId, ...options } = queryOptions ?? {}

  return useQuery({
    queryKey: [...providersKeys.current(userId), 'can-disconnect-stripe'],
    queryFn: fetchCanDisconnectStripe,
    ...options,
  })
}
